<template>
  <div
    role="radiogroup"
    :aria-label="t('settings.radioGroupSelect') + buttonsList?.label"
    class="button-group flex flex-wrap mt-1 mb-6"
  >
    <template v-for="option in buttonsList?.options" :key="option.value">
      <input
        :id="buttonsList.name + '-' + option.value"
        v-model="model"
        type="radio"
        class="hide-input absolute"
        :name="buttonsList.name"
        :value="option.value"
        :disabled="buttonsDisabled"
        @focus="handleInputFocus($event, option.value)"
        @blur="showAccessibleTooltip = false"
      />
      <label
        :for="buttonsList.name + '-' + option.value"
        class="option-name button-group__square-selector relative p-2 mx-2 my-1 cursor-pointer flex items-center justify-center"
        :title="showAccessibleTooltip ? null : option.label"
        :class="[
          buttonsDisabled ? 'button-disabled' : null,
          showOnHover || showOnFocus
            ? 'button-group__square-selector--tooltip'
            : null,
        ]"
        @mouseenter="handleShowTooltip($event, option.value)"
        @mouseleave="showAccessibleTooltip = false"
      >
        <span class="visibility-hidden absolute">{{ option.label }}</span>
        <span class="material-icons">{{ option.icon }}</span>
        <div
          v-if="showAccessibleTooltip && currentSelectedOption === option.value"
          role="tooltip"
          class="accessible-tooltip"
          aria-labelledby="accessibleTooltipTitle"
        >
          <span id="accessibleTooltipTitle">
            {{ t("settings.layoutOptions." + option.label) }}
          </span>
        </div>
      </label>
    </template>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { UserSetting } from "@/helpers/settings";
import { adjustTooltipPosition } from "@/helpers/helperFunctions";

const { t } = useI18n();
const showAccessibleTooltip = ref<boolean>(false);
const currentSelectedOption = ref<string | null>(null);

const model = defineModel<number>();

const props = defineProps({
  buttonsList: {
    type: Object as PropType<UserSetting>,
    default: null,
  },
  buttonsDisabled: {
    type: Boolean,
    default: false,
  },
  showOnHover: {
    type: Boolean,
    default: true,
  },
  showOnFocus: {
    type: Boolean,
    default: true,
  },
  uiContainer: {
    type: String,
    default: null,
  },
});

const handleShowTooltip = (event, optionName) => {
  currentSelectedOption.value = optionName;
  if (props.buttonsDisabled || !props.showOnHover) {
    showAccessibleTooltip.value = false;
    return;
  }
  showAccessibleTooltip.value = true;
  const element = event.target as HTMLElement;
  const container = element?.closest(props.uiContainer);
  if (!element) return;
  setTimeout(() => adjustTooltipPosition(element, 10, container), 0);
};

const handleInputFocus = (event, optionName) => {
  currentSelectedOption.value = optionName;
  if (!props.showOnFocus) {
    showAccessibleTooltip.value = false;
    return;
  }
  showAccessibleTooltip.value = true;
  const element = event?.target as HTMLElement;
  const container = element?.closest(props.uiContainer);
  const label = container?.querySelector(
    `label[for="${element.id}"]`
  ) as HTMLElement;
  if (!label) return;
  setTimeout(() => adjustTooltipPosition(label, 10, container), 0);
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.button-group {
  &__square-selector {
    width: 4rem;
    height: 3rem;
    border-radius: $spacingXS;
    border: $spacingXS solid var(--DM__theme-hover-color);
    transition: all 0.1s ease;

    &.button-disabled {
      color: var(--DM__color-grisFonce);
      opacity: 0.7;
      border-color: var(--DM__color-grisFonce);
      background-color: var(--DM__color-fill);
      cursor: not-allowed;
      @media (any-hover: hover) {
        &:hover {
          border-color: var(--DM__color-grisFonce);
          background-color: var(--DM__color-fill);
        }
      }
    }

    &--tooltip {
      position: relative;
      .accessible-tooltip {
        position: absolute;
        background-color: var(--DM__theme-font-color);
        color: var(--DM__theme-fill-color);
        border: 1px solid rgba(158, 158, 158, 0.2);
        box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 1rem;
        font-weight: bold;
        white-space: nowrap;
        z-index: 1;
        display: none;
        opacity: 0;
        animation: fade-in 0.1s forwards;
      }
      &:hover .accessible-tooltip,
      &:focus .accessible-tooltip {
        display: block;
      }
    }

    @media (any-hover: hover) {
      &:hover {
        border-color: var(--DM__theme-slider-color);
        background-color: var(--DM__theme-hover-color);
      }
    }
  }
  .visibility-hidden {
    z-index: -1;
  }
  .hide-input {
    opacity: 0;
    z-index: -1;
    &:focus ~ label .accessible-tooltip {
      display: block;
    }
  }

  .hide-input:checked + .option-name {
    border: 3px solid var(--DM__theme-slider-color);
    &.button-disabled {
      border-color: var(--DM__color-grisFonce);
      border-width: $spacingXS;
    }
  }

  .hide-input:focus + .option-name {
    border: $spacingS solid var(--DM__theme-border-color);
  }

  .hide-input:focus:not(:focus-visible) + .option-name {
    border: 3px solid var(--DM__theme-slider-color);
  }
}
</style>
